import { DatePipe } from '@angular/common';
import { Component, computed, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { distinctUntilChanged, map } from 'rxjs';
import { ENVIRONMENT } from '../../../../../environments/environment.interface';
import { Client } from '../../../../api/app-types/client.app';
import { DisabilityKind } from '../../../../api/app-types/disability-type.app';
import { DocumentFile, DocumentFileSubjectType } from '../../../../api/app-types/document.app';
import { LegalFoundation } from '../../../../api/app-types/legal-foundation.app';
import { ClarificationValue } from '../../../../api/app-types/relative.app';
import { CreateServiceOfficeStatementBody } from '../../../../api/app-types/service-office-statement';
import { Sponsor } from '../../../../api/app-types/sponsor.app';
import { TypeOfCare } from '../../../../api/app-types/type-of-care';
import { CATEGORIES_API } from '../../../../api/injection-tokens/services.token';
import { LEGAL_FOUNDATION_SERVICE, provideLegalFoundationApi } from '../../../../api/services/legal-foundation.service';
import { SPONSOR_SERVICE } from '../../../../api/services/sponsor.service';
import { SERVICE_OFFICE_STATEMENT_API } from '../../../../api/services/service-office-statement.service';
import { provideUploadApi } from '../../../../api/services/upload.api';
import { CUSTOM_DATE_FORMATS } from '../../../../core/adapters/custom-date-format';
import { LvrDataProtection } from '../../../../core/components/data-protection/data-protection';
import { LvrField } from '../../../../core/components/field/field';
import { LvrSearchableSelect } from '../../../../core/components/searchable-select/searchable-select';
import { LvrInputDirective } from '../../../../core/directives/lvr-input.directive';
import { DocumentUploadControl } from '../../../../shared/document-upload/document-upload';
import { allowedFileTypes, maxFiles, maxFileSize } from '../../../../shared/document-upload/document-upload-validators';
import { isValidDate, matDateProviders } from '../../../../shared/helpers/date.func';
import { hasError } from '../../../../shared/helpers/has-error.func';
import { LvrPaddingDirective } from '../../../../shared/padding/padding.directive';
import { PaginatedResponse } from '../../../../shared/pagination/paginated-response';
import { LvrAppendAsteriskPipe } from '../../../../shared/pipes/append-asterisk.pipe';
import { ClientResolved } from '../../../../shared/routing/resolvers/client.resolver';
import { RouteInjection } from '../../../../shared/routing/route-injection.class';
import { LvrAutoSaveDirective } from '../../../../shared/storage/auto-save.directive';
import { FORM_KEYS } from '../../../../shared/storage/form-keys.type';
import { FormPersistenceService } from '../../../../shared/storage/form-persistence.service';
import { LvrRestoreActions } from '../../../../shared/storage/restore-actions';
import { ImmediateErrorStateMatcher } from '../../../../shared/validators/immediate-error-state-matcher';
import { maxDateValidator, minDateValidator } from '../../../../shared/validators/max-date-validator';
import { LvrDefaultActions } from '../../../../core/components/default-actions/default.actions';
import { hasPermission } from '../../../../shared/permissions/permission.func';
import { SessionManagerService } from '../../../../shared/storage/session-manager.service';
import { RouterLink } from '@angular/router';
import { STATE } from '../../../../core/services/state.service';
import { NOTIFICATION_SERVICE } from '../../../../shared/notification/notification';
import {
	createErrorNotification,
	createSuccessNotification,
} from '../../../../shared/notification/notification.factory';

@Component({
	selector: 'lvr-create-fds-assignment',
	templateUrl: './create-fds-assignment.html',
	styleUrl: './create-fds-assignment.scss',
	imports: [
		LvrPaddingDirective,
		TranslocoPipe,
		DatePipe,
		ReactiveFormsModule,
		LvrField,
		MatFormFieldModule,
		LvrInputDirective,
		MatDatepickerModule,
		MatInput,
		MatSelectModule,
		LvrDataProtection,
		LvrSearchableSelect,
		DocumentUploadControl,
		LvrAppendAsteriskPipe,
		LvrAutoSaveDirective,
		LvrRestoreActions,
		LvrDefaultActions,
		RouterLink,
	],
	providers: [matDateProviders, provideLegalFoundationApi, provideUploadApi],
})
export class CreateFdsAssignment extends RouteInjection {
	public readonly matcher = new ImmediateErrorStateMatcher();
	public readonly dateDisplayFormat: string = CUSTOM_DATE_FORMATS.display.dateInput;
	private readonly legalFoundationService = inject(LEGAL_FOUNDATION_SERVICE);
	private readonly categoriesApi = inject(CATEGORIES_API);
	private readonly sponsorService = inject(SPONSOR_SERVICE);
	public readonly draftId = computed(() => {
		return `${FORM_KEYS.CREATE_FDS}.${this.client().id}`;
	});
	private readonly formCache = inject(FormPersistenceService);
	public readonly hasError = (control: AbstractControl | null, ...errors: string[]) => hasError(control, ...errors);
	public readonly errorStateMatcher = new ImmediateErrorStateMatcher();
	private readonly fb = inject(FormBuilder);
	private readonly env = inject(ENVIRONMENT);
	private readonly serviceOfficeStatementApi = inject(SERVICE_OFFICE_STATEMENT_API);
	private readonly ntfy = inject(NOTIFICATION_SERVICE);
	private readonly transloco = inject(TranslocoService);
	private readonly session = inject(SessionManagerService);
	private readonly state = inject(STATE);
	public readonly maxFileCount = this.env.contactSettings.maxFiles;
	public readonly maxFileSize = this.env.fileSettings.maxFileSize;
	public readonly subjectType = DocumentFileSubjectType.ASSIGNMENT;
	public readonly canCreateAssignmentsPermission = hasPermission(this.session.permissions, 'Assignments.Create');
	public readonly canManageAssignmentDocuments = hasPermission(this.session.permissions, 'Documents.Assignment.Create');
	private readonly disabilityKindPage = this.resolveDataAsSignal<PaginatedResponse<DisabilityKind>>('disabilityKinds');
	public readonly dateFormatHint = this.env.visualDateFormat;
	private readonly clientResolved = this.resolveDataAsSignal<ClientResolved>('client');
	public readonly documentsRequired = signal(false);
	public readonly client = computed(() => this.clientResolved()?.value as Client);
	public readonly typeOfCares = signal<TypeOfCare[]>([]);
	public readonly sponsors = signal<Sponsor[]>([]);
	public readonly sponsorLabel = (option: Sponsor) => option.name;
	public readonly loading = signal(false);
	public readonly disabilityKinds = computed(() => this.disabilityKindPage()?.entities ?? []);
	public readonly legalFoundationLabel = (option: LegalFoundation) => option.description;
	public readonly validDateOfBirth = computed(() => {
		return isValidDate(this.client()?.dateOfBirth);
	});
	public readonly availableLegalFoundation = signal<LegalFoundation[]>([]);
	public readonly showInitialStatementSelect = signal(false);
	public readonly minDateOfCommission = () => {
		const offset = this.env.formConfiguration.assignment.minDateOfCommissionOffsetDays;
		const threshold = new Date();
		threshold.setDate(threshold.getDate() - offset);
		threshold.setHours(0, 0, 0, 0);
		return threshold;
	};
	public readonly maxDateOfCommission = () => {
		const date = new Date();
		return date;
	};

	public readonly form = this.fb.group({
		dateOfCommission: this.fb.control<Date | null>(null, [
			Validators.required,
			minDateValidator(this.minDateOfCommission()),
			maxDateValidator(this.maxDateOfCommission()),
		]), // Datum der Beauftragung
		sponsorId: this.fb.control<number | null>(null, [Validators.required]), // Auftraggeber
		legalFoundationId: this.fb.control<number | null>(null, [Validators.required]), // Grundlage,
		initialStatement: this.fb.control<boolean | null>(null),
		careRelatedHandicapId: this.fb.control<number | null>(null), // Relevante Behinderungsart
		clarification: this.fb.control<ClarificationValue>({
			hasBeenClarified: false,
			clarifiedAt: undefined,
			typeOfClarification: undefined,
		}), // Merkblatt Sozialdatenschutz
		documents: this.fb.control<DocumentFile[]>(
			[],
			[
				maxFiles(this.maxFileCount),
				maxFileSize(this.maxFileSize),
				allowedFileTypes(this.env.fileSettings.allowedMimeTypes),
			],
		), // Dokumente
	});

	public constructor() {
		super();
		void this.loadTypeOfCares();

		effect(() => {
			const typeOfCareId = this.typeOfCareId();
			if (!typeOfCareId) {
				this.form.get('sponsorId')?.disable();
				this.form.get('legalFoundationId')?.disable();
				return;
			}
			this.form.get('sponsorId')?.enable();
			void this.loadSponsors(typeOfCareId);
		});

		effect(() => {
			const sponsorId = this.sponsorId();
			const typeOfCareId = this.typeOfCareId();
			if (!typeOfCareId) {
				this.form.get('legalFoundationId')?.disable();
				return;
			}
			if (!sponsorId) {
				this.form.get('legalFoundationId')?.disable();
				this.availableLegalFoundation.set([]);
				return;
			} else {
				this.form.get('legalFoundationId')?.enable();
			}
			this.form.get('legalFoundationId')?.setValue(null);
			this.availableLegalFoundation.set([]);
			void this.getFilteredLegalFoundations(typeOfCareId, sponsorId);
		});

		effect(() => {
			// TODO: Depending on legal foundation, enable or disable requirement of documents
			const legalFoundationId = this.legalFoundationId();
			const available = this.availableLegalFoundation();
			const docsCtrl = this.form.get('documents')!;

			if (!legalFoundationId) {
				this.setInitialStatementVisibility(false);
			} else {
				void this.loadValidTemplateAmount(legalFoundationId);
			}

			const baseValidators = [
				maxFiles(this.maxFileCount),
				maxFileSize(this.maxFileSize),
				allowedFileTypes(this.env.fileSettings.allowedMimeTypes),
			];

			if (!legalFoundationId) {
				this.documentsRequired.set(false);
				docsCtrl.setValidators(baseValidators);
				docsCtrl.updateValueAndValidity({ onlySelf: true });
				return;
			}
			const selectedLegalFoundation = available.find((foundation) => foundation.id === legalFoundationId);
			const docsRequired = !!selectedLegalFoundation?.needsDocumentForSos;
			this.documentsRequired.set(docsRequired);

			//Apply Validators, either baseValidators or with additional requiredFileValidator, depending on legal foundation
			if (docsRequired) {
				docsCtrl.setValidators([this.requiredFileValidator, ...baseValidators]);
				docsCtrl.markAsTouched();
			} else {
				docsCtrl.setValidators(baseValidators);
				docsCtrl.setErrors(null);
			}
			docsCtrl.updateValueAndValidity({ onlySelf: true, emitEvent: true });
			this.form.updateValueAndValidity({ emitEvent: true });
		});
	}

	public readonly queryParams = {
		redirectUrl: this.router.url,
	};

	public saveDraftBeforeNavigation() {
		this.formCache.saveDraft('assignment', this.draftId(), this.form.getRawValue());
	}

	public getDisabilityKindName(id: number) {
		if (!id) return '';
		return this.disabilityKinds().find((b) => b.id === id)?.disabilityType?.remark ?? '';
	}

	public getDisabilityKindDifferentiation(id: number) {
		if (!id) return '';
		return this.disabilityKinds().find((b) => b.id === id)?.categoryDiagnosis?.description ?? '-';
	}

	public cancel() {
		this.formCache.deleteDraft('fds', this.draftId());
		this.router.navigate(['../'], { relativeTo: this.route });
	}

	public async save() {
		if (this.loading()) return;

		if (this.form.invalid) {
			this.form.markAllAsTouched();
			return;
		}

		this.loading.set(true);
		try {
			const formValue = this.form.value;
			if (!formValue.dateOfCommission) {
				this.form.get('dateOfCommission')?.setErrors({ required: true });
				this.form.markAllAsTouched();
				return;
			}
			const docs = (formValue.documents ?? []) as DocumentFile[];
			const payload: CreateServiceOfficeStatementBody = {
				clientId: this.client().id,
				startOfCare: formValue.dateOfCommission!.getISODateStringWithoutTime(),
				sponsorId: formValue.sponsorId!,
				legalFoundationId: formValue.legalFoundationId!,
				careRelatedHandycapId: formValue.careRelatedHandicapId ?? null,
				socialDataProtectionInfo: formValue.clarification?.hasBeenClarified ?? null,
				documentIds: docs.map((d) => d.id),
				hasBeenClarified: formValue.clarification?.hasBeenClarified ?? undefined,
				clarifiedAt: formValue.clarification?.clarifiedAt ?? undefined,
				typeOfClarification: formValue.clarification?.typeOfClarification ?? undefined,
			};

			await this.serviceOfficeStatementApi.create(this.client().id, payload, formValue.initialStatement ?? undefined);
			await this.state.updateAssignments();
			this.formCache.deleteDraft('fds', this.draftId());
			await this.router.navigate(['../'], { relativeTo: this.route });
			this.ntfy.addNotification(
				createSuccessNotification(this.transloco.translate('base.notification_message.fds_create_success')),
			);
		} catch (error) {
			this.errorService.handleError(error, true);
			this.ntfy.addNotification(
				createErrorNotification(this.transloco.translate('base.notification_message.fds_create_error')),
			);
		} finally {
			this.loading.set(false);
		}
	}

	/**
	 * Checks if documents form control has a specific validation error and has been touched.
	 * @param control The form control to check.
	 * @param errors The name errors to look for.
	 * @returns True if any error exists and the control was touched.
	 */
	public hasFileError(control: AbstractControl | null, ...errors: string[]) {
		if (!control) return '';

		const error = errors.find(
			(error) => control.hasError(error) && (control.dirty || !!control.value?.length || control.touched),
		);

		if (!error) return '';
		return error;
	}

	private readonly sponsorId = toSignal(
		this.form.get('sponsorId')!.valueChanges.pipe(
			distinctUntilChanged(),
			map(() => this.form.get('sponsorId')!.value as number | undefined),
		),
		{
			initialValue: undefined,
		},
	);

	private readonly legalFoundationId = toSignal(
		this.form.get('legalFoundationId')!.valueChanges.pipe(
			distinctUntilChanged(),
			map(() => this.form.get('legalFoundationId')!.value as number | undefined),
		),
		{
			initialValue: undefined,
		},
	);

	private readonly typeOfCareId = signal<number | undefined>(undefined);

	/**
	 * Gets the legal foundations for the current type of care id
	 * and returns only those that belong to the current selected sponsor.
	 * @returns A filtered legal foundation list.
	 */
	private async getFilteredLegalFoundations(typeOfCareId?: number, sponsorId?: number) {
		const legalFoundationRange = this.env.pagination.range;
		if (!typeOfCareId || !sponsorId) {
			this.availableLegalFoundation.set([]);
			return;
		}

		const res = await this.legalFoundationService.getAllForTypeOfCareAndSponsor(
			typeOfCareId,
			sponsorId,
			undefined,
			legalFoundationRange,
		);
		const all = (res?.entities ?? res?.entities ?? []) as LegalFoundation[];
		this.availableLegalFoundation.set(all);
	}

	private async loadValidTemplateAmount(legalFoundationId: number) {
		try {
			const amount = await this.legalFoundationService.getValidTemplatesAmount(legalFoundationId);
			this.setInitialStatementVisibility(amount > 1);
		} catch (error) {
			this.setInitialStatementVisibility(false);
			this.errorService.handleError(error, false);
		}
	}

	private setInitialStatementVisibility(visible: boolean) {
		this.showInitialStatementSelect.set(visible);
		const ctrl = this.form.get('initialStatement');
		if (!ctrl) return;
		if (visible) {
			ctrl.setValidators([Validators.required]);
		} else {
			ctrl.clearValidators();
		}
		ctrl.setValue(null);
		ctrl.updateValueAndValidity({ onlySelf: true });
	}

	private async loadTypeOfCares() {
		const res = await this.categoriesApi.getAllTypesOfCare(
			this.env.pagination.offset,
			this.env.pagination.range,
			true,
			false,
			false,
		);
		const items = res.typesOfCare ?? [];
		this.typeOfCares.set(items);
		this.typeOfCareId.set(items[0]?.id);
	}

	private async loadSponsors(typeOfCareId?: number) {
		if (!typeOfCareId) {
			this.sponsors.set([]);
			this.form.get('sponsorId')?.setValue(null);
			this.form.get('legalFoundationId')?.setValue(null);
			return;
		}
		const res = await this.sponsorService.getAllSponsorsForTypeOfCare(typeOfCareId);
		this.sponsors.set(res.entities ?? []);
		this.form.get('sponsorId')?.setValue(null);
		this.form.get('legalFoundationId')?.setValue(null);
	}

	private readonly requiredFileValidator: ValidatorFn = (control) => {
		const value = control.value;
		if (!value || !Array.isArray(value) || value.length === 0) {
			return { requiredFile: true };
		}
		return null;
	};
}
